// src/components/common/BotCheckDialog.js
// Optional human check (ALTCHA, open source, self-hosted proof-of-work). Only shown when
// the backend has BOT_CHECK_PROVIDER=altcha; the widget script is loaded on demand.
import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { BOTCHECK_CHALLENGE_URL, verifyHuman } from "../../api";
import { friendlyError } from "../../utils/errors";

export default function BotCheckDialog({ open, onClose, onVerified }) {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    import("altcha").then(() => !cancelled && setReady(true)).catch(() => setError("The check could not be loaded."));
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !ready) return undefined;
    const onState = async (ev) => {
      if (ev.detail?.state === "verified" && ev.detail.payload) {
        try {
          await verifyHuman(ev.detail.payload);
          onVerified();
        } catch (err) {
          setError(friendlyError(err));
        }
      }
    };
    el.addEventListener("statechange", onState);
    return () => el.removeEventListener("statechange", onState);
  }, [ready, onVerified]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="botcheck-title">
      <DialogTitle id="botcheck-title">Quick check before the AI runs</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          To protect this free tool from automated abuse, please confirm you are human. Your browser solves a small
          puzzle; no data is shared with third parties.
        </Typography>
        <Box sx={{ minHeight: 70 }}>
          {ready && <altcha-widget ref={ref} challengeurl={BOTCHECK_CHALLENGE_URL} hidefooter="false" />}
        </Box>
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancel</Button></DialogActions>
    </Dialog>
  );
}
